import React, { useMemo, useState, useRef, useEffect } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { parseBirthYear } from "./FilterPanel";
import { normalizeName, squashName, nameMatches, rankByNameStart } from "../../shared/nameSearch";
import { useTheme } from "../../../context/ThemeContext";

function PersonPicker({ familyTreeData, onSelect, label, value, filters }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [inputText, setInputText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(100);
  const containerRef = useRef(null);

  // When a value is selected externally (or cleared externally), sync input text
  useEffect(() => {
    if (value) {
      setInputText(value.label || "");
      setIsOpen(false);
    } else {
      setInputText("");
    }
  }, [value]);

  // Close dropdown when clicking outside this component
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Build the list of searchable options from the family tree data.
  // We pre-compute a normalizedLabel so we only do the accent-stripping work once
  // (instead of on every keystroke when 7,600+ people need filtering).
  const options = useMemo(() => {
    if (!familyTreeData) return [];

    return familyTreeData.map((person) => {
      const first = person.data.first_name || "";
      const last = person.data.last_name || "";
      const suffix = person.data.numeric_suffix || "";
      const displayName = [first, last, suffix].filter(Boolean).join(" ");
      const searchText = [
        displayName,
        person.data.married_name,
        person.data.searchable_married_name,
      ]
        .filter(Boolean)
        .join(" ");
      const birthYear = person.data.birthday
        ? `b. ${person.data.birthday}`
        : "";
      return {
        id: person.id,
        label: displayName,
        // Search-only copies: accents and apostrophes stripped (and spaces,
        // for squashedLabel). The label shown on screen is unchanged.
        // Same married-name fields the Family Tree search uses
        // (TreeView.jsx:287-294), so a woman is also found under the surname
        // she married into. Search only — never shown.
        normalizedLabel: normalizeName(searchText),
        squashedLabel: squashName(searchText),
        subtitle: birthYear,
        person,
      };
    });
  }, [familyTreeData]);

  // Filter options: first by generation/era filters, then by typed text
  const filteredOptions = useMemo(() => {
    let filtered = options;

    // Apply generation and birth-era filters (if any are active)
    if (filters) {
      const { generations, eras } = filters;

      if (generations && generations.length > 0) {
        const genSet = new Set(generations);
        filtered = filtered.filter((o) =>
          genSet.has(o.person.data.generation)
        );
      }

      if (eras && eras.length > 0) {
        filtered = filtered.filter((o) => {
          const year = parseBirthYear(o.person.data.birthday);
          if (year === null) return eras.includes("Unknown");
          return eras.some((era) => {
            if (era === "Unknown") return false;
            const [startStr] = era.split("\u2013");
            const start = Number(startStr);
            return year >= start && year < start + 25;
          });
        });
      }
    }

    // Apply text search — split into individual words so each word
    // can match anywhere in the name, in any order.
    // e.g. "du pont pierre" matches "Pierre Samuel du Pont de Nemours"
    if (!inputText.trim()) return filtered;

    const results = [];
    for (let i = 0; i < filtered.length; i++) {
      const { normalizedLabel, squashedLabel } = filtered[i];
      if (nameMatches(inputText, normalizedLabel, squashedLabel)) {
        results.push(filtered[i]);
      }
    }
    // Same matches, better order: a name word that STARTS with the typed text
    // comes first ("o'do" puts O'Dowd above Theodore), the rest after.
    return rankByNameStart(inputText, results, (o) => o.normalizedLabel);
  }, [options, filters, inputText]);

  // Check if any filters are active (used for the "no options" message)
  const hasActiveFilters =
    filters &&
    ((filters.generations && filters.generations.length > 0) ||
      (filters.eras && filters.eras.length > 0));

  function handleInputChange(e) {
    const text = e.target.value;
    setInputText(text);
    setIsOpen(true);
    setDisplayLimit(100); // Reset to first 100 when typing
    // If user starts typing after selecting, clear the selection
    if (value) {
      onSelect(null);
    }
  }

  function handleSelect(option) {
    setInputText(option.label);
    setIsOpen(false);
    onSelect(option);
  }

  function handleClear() {
    setInputText("");
    setIsOpen(true);
    onSelect(null);
  }

  function handleFocus() {
    // Only open the dropdown if nothing is selected yet
    if (!value) {
      setIsOpen(true);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative", minWidth: 250, flex: 1 }}>
      <TextField
        label={label}
        variant="outlined"
        size="small"
        fullWidth
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
        autoComplete="off"
        slotProps={{
          input: {
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: isDark ? "#1e293b" : "white",
            borderRadius: "8px",
            color: isDark ? "#e2e8f0" : "#1a3a5c",
          },
          "& .MuiInputLabel-root": {
            color: "#b8860b",
            fontWeight: 600,
            "&.Mui-focused": {
              color: "#b8860b",
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "rgba(184, 134, 11, 0.4)" : "rgba(184, 134, 11, 0.3)",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(184, 134, 11, 0.6)",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#b8860b",
          },
          "& .MuiSvgIcon-root": {
            color: isDark ? "#94a3b8" : undefined,
          },
        }}
      />

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: 300,
            overflowY: "auto",
            backgroundColor: isDark ? "#1e293b" : "white",
            color: isDark ? "#e2e8f0" : "#1a3a5c",
            border: isDark ? "1px solid rgba(184, 134, 11, 0.3)" : "1px solid #ccc",
            borderRadius: "0 0 8px 8px",
            boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1300,
          }}
        >
          {filteredOptions.length > 0 ? (
            <>
              {filteredOptions.slice(0, displayLimit).map((option) => (
                <div
                  key={option.id}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur from firing before click
                    handleSelect(option);
                  }}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                  }}
                  className="hover:bg-dupontBlue/10"
                >
                  <div className="flex flex-col">
                    <span>{option.label}</span>
                    {option.subtitle && (
                      <span className="text-[13px] text-gray-400">
                        {option.subtitle}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {filteredOptions.length > displayLimit && (
                <div
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setDisplayLimit((prev) => prev + 100);
                  }}
                  style={{
                    padding: "10px 16px",
                    cursor: "pointer",
                    borderTop: "1px solid #eee",
                    textAlign: "center",
                    fontSize: "13px",
                    color: "#1a3a5c",
                    fontWeight: 500,
                  }}
                  className="hover:bg-dupontBlue/5"
                >
                  Show more results ({filteredOptions.length - displayLimit} remaining)
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                padding: "12px 16px",
                color: "#999",
                fontSize: "14px",
              }}
            >
              {hasActiveFilters
                ? "No matches found \u2014 try adjusting your filters"
                : "No options"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PersonPicker;
